import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { UserRole } from '@types/index';

// Validation schema - accepts email, player ID, or coach ID
const loginSchema = z.object({
  email: z.string().min(1, 'Email, Player ID, or Coach ID is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      console.log('LoginPage - Attempting login with:', { email: data.email });
      const response = await authService.login(data);
      console.log('LoginPage - Login response received:', response);
      console.log('LoginPage - User object:', response.user);
      console.log('LoginPage - User academyId:', response.user.academyId);
      login(response.user);
      
      // Debug: Check what role is received
      console.log('LoginPage - Login successful, user role:', response.user.role);
      console.log('LoginPage - UserRole.COACH:', UserRole.COACH);
      console.log('LoginPage - Role match:', response.user.role === UserRole.COACH);
      
      toast.success('Login successful!');
      
      // Redirect based on role
      const role = response.user.role;
      if (role === 'academy_admin') {
        navigate('/academy-admin/dashboard');
      } else if (role === UserRole.SPONSOR || role === 'sponsor') {
        navigate('/sponsor/dashboard');
      } else if (role === UserRole.ADMIN || role === 'admin' ||
                 role === UserRole.SUPER_ADMIN || role === 'super_admin' ||
                 role === UserRole.EVALUATION_ADMIN || role === 'evaluation_admin' ||
                 role === UserRole.CONTEST_ADMIN || role === 'contest_admin') {
        navigate('/admin');
      } else if (role === UserRole.COACH || role === 'coach') {
        navigate('/coach/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('LoginPage - Login error:', err);
      console.error('LoginPage - Error response:', err.response);
      console.error('LoginPage - Error message:', err.message);
      console.error('LoginPage - Error data:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          marginTop: { xs: 4, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: { xs: 2.5, sm: 4 }, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            EncycGames
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" gutterBottom>
            Sign in to your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <TextField
              {...register('email')}
              label="Email, Player ID, or Coach ID"
              fullWidth
              margin="normal"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              {...register('password')}
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/forgot-password" sx={{ display: 'block', mb: 2 }}>
                <Typography variant="body2" color="primary">
                  Forgot Password?
                </Typography>
              </Link>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Don't have an account?{' '}
                <Link component={RouterLink} to="/register/player">
                  Register as Player
                </Link>
                {' or '}
                <Link component={RouterLink} to="/register/coach">
                  Register as Coach
                </Link>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <Link component={RouterLink} to="/register/sponsor" sx={{ fontWeight: 600 }}>
                  Register as Sponsor Partner
                </Link>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <Link component={RouterLink} to="/register/academy" sx={{ fontWeight: 600 }}>
                  Register Regular School or Sports Academy
                </Link>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                <Link component={RouterLink} to="/sponsors" sx={{ fontWeight: 600 }}>
                  Partner with EncycGames as a sponsor
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;


